
import React, { useState, useMemo } from 'react';
import { ShieldCheck, Plus, Trash2, TrendingUp, Landmark, Waves, Briefcase, Zap, Info, ChevronRight, BarChart3, PieChart } from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, CartesianGrid, Pie, PieChart as RePieChart } from 'recharts';
import { WealthItem, WealthMetrics } from '../../types';

interface WealthAnalyzerProps {
  items: WealthItem[];
  metrics: WealthMetrics;
  onAdd: (name: string, amount: number, type: 'asset' | 'liability', subType: string) => void;
  onDelete: (id: string) => void;
}

export const WealthAnalyzer = ({ items, metrics, onAdd, onDelete }: WealthAnalyzerProps) => {
  const [isAdding, setIsAdding] = useState(false);
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [type, setType] = useState<'asset' | 'liability'>('asset');
  const [subType, setSubType] = useState<'liquid' | 'investment' | 'fixed' | 'debt'>('liquid');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !amount) return;
    onAdd(name, parseFloat(amount), type, subType);
    setName('');
    setAmount('');
    setIsAdding(false);
  };

  const waterfallData = [
    { name: 'Liquid', value: items.filter(i => i.subType === 'liquid').reduce((s, i) => s + i.amount, 0), color: '#87CEEB' },
    { name: 'Growth', value: items.filter(i => i.subType === 'investment').reduce((s, i) => s + i.amount, 0), color: '#10B981' },
    { name: 'Physical', value: items.filter(i => i.subType === 'fixed').reduce((s, i) => s + i.amount, 0), color: '#001F3F' },
    { name: 'Debt', value: -metrics.totalLiabilities, color: '#F43F5E' }
  ];

  const subTypeBreakdown = useMemo(() => {
    const assets = items.filter(i => i.type === 'asset');
    const liabs = items.filter(i => i.type === 'liability');

    const totalAssetVal = assets.reduce((s, i) => s + i.amount, 0) || 1;
    const totalLiabVal = liabs.reduce((s, i) => s + i.amount, 0) || 1;

    return {
      assets: [
        { label: 'Liquid', amount: assets.filter(i => i.subType === 'liquid').reduce((s, i) => s + i.amount, 0), color: '#87CEEB', percent: 0 },
        { label: 'Investment', amount: assets.filter(i => i.subType === 'investment').reduce((s, i) => s + i.amount, 0), color: '#10B981', percent: 0 },
        { label: 'Fixed', amount: assets.filter(i => i.subType === 'fixed').reduce((s, i) => s + i.amount, 0), color: '#001F3F', percent: 0 },
      ].map(i => ({ ...i, percent: Math.round((i.amount / totalAssetVal) * 100) })),
      liabilities: [
        { label: 'Debt', amount: liabs.filter(i => i.subType === 'debt').reduce((s, i) => s + i.amount, 0), color: '#F43F5E', percent: 0 },
      ].map(i => ({ ...i, percent: Math.round((i.amount / totalLiabVal) * 100) }))
    };
  }, [items]);

  return (
    <div className="space-y-8 animate-fade-in pb-12">
      <div className="fin-card p-10 rounded-4xl">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-8 mb-12">
          <div className="flex items-center gap-5">
            <div className="p-4 bg-emerald/10 text-emerald rounded-2xl">
              <ShieldCheck size={32} />
            </div>
            <div>
              <h2 className="text-3xl font-extrabold text-navy tracking-tight">Wealth Analyzer</h2>
              <p className="text-xs font-inter text-slate-400 uppercase font-bold tracking-widest">Equity Management</p>
            </div>
          </div>

          <div className="text-right">
            <div className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mb-1">Portfolio NAV</div>
            <div className="text-4xl font-extrabold text-navy tracking-tighter">R{metrics.nav.toLocaleString()}</div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <WealthStat icon={<Waves className="text-emerald" size={18} />} label="Liquidity" value={`R${metrics.liquidity.toLocaleString()}`} sub="Available Cash" />
          <WealthStat icon={<TrendingUp className="text-sky" size={18} />} label="Asset Ratio" value={`${metrics.debtRatio.toFixed(1)}%`} sub="Leverage Depth" />
          <WealthStat icon={<Zap className="text-emerald" size={18} />} label="Independence" value={`${metrics.freedomProgress.toFixed(1)}%`} sub="Journey Progress" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="fin-card p-8 rounded-3xl flex flex-col">
          <div className="flex justify-between items-center mb-10">
            <h3 className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Wealth Stack</h3>
            <span className="text-[10px] font-bold text-slate-300 uppercase tracking-widest">NAV Allocation</span>
          </div>
          <div className="flex-grow min-h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={waterfallData} margin={{ top: 20, right: 0, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                <XAxis dataKey="name" stroke="#94A3B8" fontSize={10} axisLine={false} tickLine={false} />
                <YAxis fontSize={10} stroke="#94A3B8" axisLine={false} tickLine={false} tickFormatter={(v) => `R${Math.abs(v/1000)}k`} />
                <Tooltip 
                  cursor={{fill: '#F8FAFC'}} 
                  contentStyle={{backgroundColor: '#fff', border: '1px solid #F1F5F9', borderRadius: '12px', fontSize: '12px'}} 
                  formatter={(val: any) => [`R${Math.abs(Number(val)).toLocaleString()}`, 'Value']}
                />
                <Bar dataKey="value" radius={[6, 6, 6, 6]} barSize={48}>
                  {waterfallData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="fin-card p-8 rounded-3xl">
          <div className="flex justify-between items-center mb-8">
            <h3 className="text-[10px] font-inter font-extrabold text-slate-400 uppercase tracking-widest">Asset Inventory</h3>
            <button 
              onClick={() => setIsAdding(!isAdding)}
              className={`p-2 rounded-xl transition-all ${isAdding ? 'bg-navy text-white' : 'bg-slate-50 text-navy hover:bg-emerald/10'}`}
            >
              <Plus size={22} />
            </button>
          </div>

          {isAdding && (
            <form onSubmit={handleSubmit} className="mb-8 space-y-4 p-6 bg-slate-50 border border-slate-100 rounded-3xl animate-fade-in">
              <input 
                type="text" 
                placeholder="Name (e.g. Home, Bitcoin, Car Loan)" 
                value={name} 
                onChange={e => setName(e.target.value)}
                className="w-full bg-white border border-slate-200 p-4 rounded-2xl text-base text-navy font-semibold focus:border-emerald outline-none"
              />
              <div className="grid grid-cols-2 gap-4">
                <input 
                  type="number" 
                  placeholder="Amount" 
                  value={amount} 
                  onChange={e => setAmount(e.target.value)}
                  className="bg-white border border-slate-200 p-4 rounded-2xl text-base text-navy font-bold focus:border-emerald outline-none"
                />
                <select 
                  value={type} 
                  onChange={e => {
                    const val = e.target.value as any;
                    setType(val);
                    setSubType(val === 'liability' ? 'debt' : 'liquid');
                  }}
                  className="bg-white border border-slate-200 p-4 rounded-2xl text-base text-navy font-bold focus:border-emerald outline-none active:scale-[0.98] transition-all"
                >
                  <option value="asset">Asset</option>
                  <option value="liability">Liability</option>
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-widest ml-1">Composition Sub-type</label>
                <select 
                  value={subType} 
                  onChange={e => setSubType(e.target.value as any)}
                  className="w-full bg-white border border-slate-200 p-4 rounded-2xl text-base text-navy font-bold focus:border-electric outline-none active:scale-[0.98] transition-all"
                >
                  {type === 'asset' ? (
                    <>
                      <option value="liquid">Liquid (Cash/Bank)</option>
                      <option value="investment">Investment (Stocks/Crypto)</option>
                      <option value="fixed">Fixed (Property/Vehicle)</option>
                    </>
                  ) : (
                    <option value="debt">Debt (Loan/Credit)</option>
                  )}
                </select>
              </div>
              <button type="submit" className="w-full py-4 bg-navy text-white rounded-2xl font-bold hover:bg-emerald transition-colors shadow-lg shadow-navy/10">
                Register Value
              </button>
            </form>
          )}

          <div className="space-y-4 max-h-[340px] overflow-y-auto custom-scrollbar pr-2">
            {items.length === 0 ? (
              <div className="py-20 text-center text-slate-300">
                <p className="text-sm font-bold uppercase tracking-widest">No assets registered</p>
              </div>
            ) : (
              items.map(item => (
                <div key={item.id} className="p-5 bg-white border border-slate-50 rounded-2xl flex justify-between items-center group hover:border-slate-100 transition-all">
                  <div className="flex items-center gap-4">
                    <div className={`p-2.5 rounded-xl ${item.type === 'asset' ? 'bg-emerald/10 text-emerald' : 'bg-rose-50 text-rose-500'}`}>
                      {item.subType === 'liquid' ? <Waves size={18} /> : item.subType === 'investment' ? <TrendingUp size={18} /> : item.subType === 'fixed' ? <Landmark size={18} /> : <Briefcase size={18} />}
                    </div>
                    <div>
                      <div className="text-sm font-bold text-navy tracking-tight">{item.name}</div>
                      <div className="text-[9px] font-inter text-slate-400 uppercase font-bold tracking-widest">{item.subType}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-6">
                    <div className={`text-base font-bold tracking-tight ${item.type === 'asset' ? 'text-emerald' : 'text-rose-500'}`}>
                      {item.type === 'asset' ? '+' : '-'}R{item.amount.toLocaleString()}
                    </div>
                    <button onClick={() => onDelete(item.id)} className="text-slate-200 hover:text-rose-500 transition-colors opacity-0 group-hover:opacity-100">
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* New Sub-type Breakdown Card */}
        <div className="fin-card p-8 rounded-3xl lg:col-span-2">
          <div className="flex items-center gap-3 mb-10">
            <div className="p-2.5 bg-slate-50 text-navy rounded-xl">
              <PieChart size={20} />
            </div>
            <div>
              <h3 className="text-[10px] font-extrabold text-slate-400 uppercase tracking-widest">Composition Audit</h3>
              <p className="text-sm font-bold text-navy tracking-tight">Internal Sub-type Distribution</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div className="space-y-6">
              <h4 className="text-[9px] font-black text-electric uppercase tracking-widest mb-4">Asset Quality</h4>
              {subTypeBreakdown.assets.map(item => (
                <div key={item.label} className="space-y-2">
                  <div className="flex justify-between items-center text-xs">
                    <span className="font-bold text-navy">{item.label}</span>
                    <span className="font-mono text-slate-400">R{item.amount.toLocaleString()} ({item.percent}%)</span>
                  </div>
                  <div className="h-1.5 w-full bg-slate-50 rounded-full overflow-hidden border border-slate-100">
                    <div className="h-full transition-all duration-1000" style={{ backgroundColor: item.color, width: `${item.percent}%` }} />
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-6">
              <h4 className="text-[9px] font-black text-rose-500 uppercase tracking-widest mb-4">Liability Structure</h4>
              {subTypeBreakdown.liabilities.length > 0 ? (
                subTypeBreakdown.liabilities.map(item => (
                  <div key={item.label} className="space-y-2">
                    <div className="flex justify-between items-center text-xs">
                      <span className="font-bold text-navy">{item.label}</span>
                      <span className="font-mono text-slate-400">R{item.amount.toLocaleString()} ({item.percent}%)</span>
                    </div>
                    <div className="h-1.5 w-full bg-slate-50 rounded-full overflow-hidden border border-slate-100">
                      <div className="h-full transition-all duration-1000" style={{ backgroundColor: item.color, width: `${item.percent}%` }} />
                    </div>
                  </div>
                ))
              ) : (
                <div className="h-20 flex items-center justify-center text-slate-300 text-[10px] uppercase font-bold tracking-widest border-2 border-dashed border-slate-50 rounded-2xl">
                  No Liabilities Logged
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const WealthStat = ({ icon, label, value, sub }: any) => (
  <div className="bg-slate-50 p-6 rounded-3xl hover:bg-slate-100/50 transition-all group border border-transparent hover:border-slate-100">
    <div className="flex items-center gap-3 mb-4">
      <div className="p-2.5 bg-white rounded-xl shadow-sm group-hover:shadow-md transition-shadow">
        {icon}
      </div>
      <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">{label}</span>
    </div>
    <div className="text-2xl font-extrabold text-navy mb-1 tracking-tight">{value}</div>
    <div className="text-[10px] text-slate-400 font-bold uppercase tracking-tight opacity-70">{sub}</div>
  </div>
);
